import { formatDocument, formatPhone, formatRandomPixKey, formatZipCode, PixKeyType } from '../../helpers'

/** As máscaras de texto do painel. Dinheiro não está aqui: é o `<MoneyInput>`, que tem valor numérico. */
export type TMask = 'phone' | 'cpf' | 'cpfCnpj' | 'email' | 'zipCode' | 'pixRandom'

interface IMaskSpec {
  /**
   * Precisa ser idempotente — `format(format(x)) === format(x)` —, porque o componente formata
   * tanto o que chega por prop (que pode vir cru da API) quanto o que é digitado.
   */
  format: (value: string) => string
  placeholder: string
  /** O comprimento do valor **formatado**, que é o que o `<input>` mede. */
  maxLength: number
  inputMode: 'numeric' | 'tel' | 'text' | 'email'
  autoComplete?: string
  type?: 'tel'
}

/**
 * A aparência e a geometria de cada máscara, num lugar só.
 *
 * Isto é o que estava espalhado: dois `placeholder` de telefone (`(00) 00000-0000` em 6 campos e
 * `(11) 99999-9999` em 3), `maxLength` em 20, 15 e nenhum, e `type`/`inputMode`/`autoComplete` em
 * 2 dos 13 campos de telefone. O teclado numérico no celular dependia de qual página o usuário
 * tinha aberto.
 */
export const MASKS: Record<TMask, IMaskSpec> = {
  phone: {
    format: formatPhone,
    placeholder: '(00) 00000-0000',
    // `+999 (99) 99999-9999` — o ramo mais longo de `formatPhone`, com código de país.
    maxLength: 20,
    inputMode: 'tel',
    autoComplete: 'tel',
    type: 'tel'
  },
  cpf: {
    format: (value) => formatDocument(value, true),
    placeholder: '000.000.000-00',
    maxLength: 14,
    inputMode: 'numeric'
  },
  cpfCnpj: {
    format: (value) => formatDocument(value),
    placeholder: '000.000.000-00',
    // `00.000.000/0000-00` — o CNPJ, que é o mais longo dos dois.
    maxLength: 18,
    inputMode: 'numeric'
  },
  // O e-mail não tem forma fixa, mas tem teclado e comprimento próprios: é a "máscara" de
  // um dos quatro tipos de chave Pix, e está aqui para que os quatro saiam do mesmo lugar.
  email: {
    format: (value) => value,
    placeholder: 'nome@dominio.com.br',
    maxLength: 255,
    inputMode: 'email'
  },
  zipCode: {
    format: formatZipCode,
    placeholder: '00000-000',
    maxLength: 9,
    inputMode: 'numeric',
    autoComplete: 'postal-code'
  },
  pixRandom: {
    format: formatRandomPixKey,
    placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    maxLength: 36,
    inputMode: 'text'
  }
}

/**
 * Qual máscara cada tipo de chave Pix usa — o campo de chave troca de máscara conforme o select
 * de tipo ao lado dele.
 *
 * O `EventCouponFormModal` tinha um `PIX_KEY_FIELD` próprio com exatamente estes três atributos
 * (placeholder, `maxLength`, `inputMode`) por tipo, ao lado de um `formatPixKey` nos helpers que
 * tinha o quarto. Eram dois registros paralelos da mesma coisa.
 */
export const PIX_KEY_MASKS: Record<PixKeyType, TMask> = {
  cpf_cnpj: 'cpfCnpj',
  email: 'email',
  phone: 'phone',
  random: 'pixRandom'
}
